/**
 * Session Activity Context
 * 
 * Manages user inactivity detection and tiered session locking:
 * - <5 mins: No lock, preserve full session state
 * - 5-30 mins: Soft lock (modal overlay, session preserved)
 * - 30-60 mins: Brand lock (preserve brand, navigate to brand home)
 * - 1hr+: User lock (preserve user only, navigate to user home)
 */

"use client";

import {
	createContext,
	useContext,
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactNode,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { signOut } from "next-auth/react";
import { useBrandSwitch } from "@/contexts/brand-switch-context";

// =============================================
// Types
// =============================================

export type SessionLockLevel = "none" | "soft" | "brand" | "user";

export interface SessionState {
	/** Current lock level based on inactivity */
	lockLevel: SessionLockLevel;
	/** Whether the lock screen modal should be shown */
	isLocked: boolean;
	/** Time of last user activity (mouse, keyboard, touch) */
	lastActivityAt: Date;
	/** Seconds until next lock level */
	secondsUntilLock: number;
	/** The page path to restore after unlock */
	preservedPath: string | null;
	/** The brand ID to restore after unlock */
	preservedBrandId: string | null;
	/** Additional page state (scroll position, filters) */
	preservedPageState: Record<string, unknown>;
}

export interface SessionActivityContextValue extends SessionState {
	/** Call this on any user interaction to reset the timer */
	recordActivity: () => void;
	/** Unlock the session after successful auth */
	unlock: () => void;
	/** Force lock (e.g., user clicks "Lock" button) */
	forceLock: () => void;
	/** Save current page state for restoration */
	savePageState: (state: Record<string, unknown>) => void;
}

// =============================================
// Constants
// =============================================

/** Inactivity thresholds in milliseconds */
export const INACTIVITY_THRESHOLDS = {
	/** No lock threshold - under this, no action taken */
	NONE: 5 * 60 * 1000, // 5 minutes
	/** Soft lock - show modal, preserve everything */
	SOFT: 30 * 60 * 1000, // 30 minutes
	/** Brand lock - preserve brand, reset page */
	BRAND: 60 * 60 * 1000, // 1 hour
	/** User lock - preserve user only */
	USER: 60 * 60 * 1000, // 1 hour (same threshold, different treatment)
} as const;

/** How often to check inactivity (ms) */
const CHECK_INTERVAL = 10 * 1000; // 10 seconds

/** How often to sync activity to server (ms) */
const SYNC_INTERVAL = 60 * 1000; // 1 minute

/** Storage key for session state */
const STORAGE_KEY = "vastipost_session_state";

// =============================================
// Context
// =============================================

const SessionActivityContext = createContext<SessionActivityContextValue | null>(null);

export function useSessionActivity() {
	const context = useContext(SessionActivityContext);
	if (!context) {
		throw new Error("useSessionActivity must be used within SessionActivityProvider");
	}
	return context;
}

// =============================================
// Provider
// =============================================

interface SessionActivityProviderProps {
	children: ReactNode;
	/** User's default home page (for user-level lock) */
	userDefaultHome?: string;
	/** Current brand's default home page (for brand-level lock) */
	brandDefaultHome?: string;
}

export function SessionActivityProvider({
	children,
	userDefaultHome = "/analytics",
	brandDefaultHome = "/content",
}: SessionActivityProviderProps) {
	const router = useRouter();
	const pathname = usePathname();
	const { activeBrandId } = useBrandSwitch();
	
	// Disable session activity tracking on home/login/auth pages
	const isDisabledPage = pathname === "/" || pathname?.startsWith("/login") || pathname?.startsWith("/signup") || pathname?.startsWith("/auth") || pathname?.startsWith("/logout");
	
	// State
	const [lockLevel, setLockLevel] = useState<SessionLockLevel>("none");
	const [isLocked, setIsLocked] = useState(false);
	const [lastActivityAt, setLastActivityAt] = useState(() => new Date());
	const [secondsUntilLock, setSecondsUntilLock] = useState(INACTIVITY_THRESHOLDS.NONE / 1000);
	const [preservedPath, setPreservedPath] = useState<string | null>(null);
	const [preservedBrandId, setPreservedBrandId] = useState<string | null>(null);
	const [preservedPageState, setPreservedPageState] = useState<Record<string, unknown>>({});
	
	// Refs for intervals
	const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const lastSyncRef = useRef<Date>(new Date());
	
	// =============================================
	// Activity Recording
	// =============================================
	
	const recordActivity = useCallback(() => {
		const now = new Date();
		setLastActivityAt(now);
		
		// If we were locked, don't auto-unlock - require re-auth
		if (isLocked) return;
		
		// Reset lock level if we were approaching one
		if (lockLevel !== "none") {
			setLockLevel("none");
		}
		
		// Sync to server periodically
		if (now.getTime() - lastSyncRef.current.getTime() > SYNC_INTERVAL) {
			lastSyncRef.current = now;
			syncActivityToServer(now);
		}
	}, [isLocked, lockLevel]);
	
	// =============================================
	// Server Sync
	// =============================================
	
	const syncActivityToServer = useCallback(async (activityTime: Date) => {
		try {
			const res = await fetch("/api/auth/activity", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					lastActivityAt: activityTime.toISOString(),
					currentPath: pathname,
					currentBrandId: activeBrandId,
					pageState: preservedPageState,
				}),
			});

			// Check if server is forcing this session to log out
			// (e.g., exceeded 3 active-session limit or manually revoked)
			if (res.ok) {
				const data = await res.json();
				if (data.forceLogout) {
					console.warn("[SessionActivity] Server requested force logout — signing out");
					await signOut({ redirect: true, callbackUrl: "/" });
					return;
				}
			}
		} catch (error) {
			// Silently fail - activity sync is best-effort
			console.debug("Failed to sync activity:", error);
		}
	}, [pathname, activeBrandId, preservedPageState]);
	
	// =============================================
	// Lock Level Calculation
	// =============================================
	
	const calculateLockLevel = useCallback((inactivityMs: number): SessionLockLevel => {
		if (inactivityMs >= INACTIVITY_THRESHOLDS.BRAND) {
			return "user";
		} else if (inactivityMs >= INACTIVITY_THRESHOLDS.SOFT) {
			return "brand";
		} else if (inactivityMs >= INACTIVITY_THRESHOLDS.NONE) {
			return "soft";
		}
		return "none";
	}, []);
	
	// =============================================
	// Lock Handling
	// =============================================
	
	const applyLock = useCallback((newLevel: SessionLockLevel) => {
		if (newLevel === "none") return;
		
		// Preserve current state before locking
		if (!preservedPath) {
			setPreservedPath(pathname);
		}
		if (!preservedBrandId && activeBrandId) {
			setPreservedBrandId(activeBrandId);
		}
		
		setLockLevel(newLevel);
		setIsLocked(true);
		
		// Save to session storage for persistence across page reloads
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
				lockLevel: newLevel,
				preservedPath: pathname,
				preservedBrandId: activeBrandId,
				preservedPageState,
				lockedAt: new Date().toISOString(),
			}));
		} catch (error) {
			console.debug("Failed to save session state:", error);
		}
		
		// Sync lock state to server
		syncLockToServer(newLevel);
	}, [pathname, activeBrandId, preservedPath, preservedBrandId, preservedPageState]);
	
	const syncLockToServer = useCallback(async (level: SessionLockLevel) => {
		try {
			await fetch("/api/auth/lock", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					lockLevel: level,
					lastBrandId: activeBrandId,
					lastPagePath: pathname,
					lastPageState: preservedPageState,
				}),
			});
		} catch (error) {
			console.debug("Failed to sync lock state:", error);
		}
	}, [activeBrandId, pathname, preservedPageState]);
	
	// =============================================
	// Unlock
	// =============================================
	
	const unlock = useCallback(() => {
		setIsLocked(false);
		setLockLevel("none");
		setLastActivityAt(new Date());
		
		// Clear stored state
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch (error) {
			console.debug("Failed to clear session state:", error);
		}
		
		// Navigate based on previous lock level
		const prevLevel = lockLevel;
		
		if (prevLevel === "user") {
			// User-level lock: go to user home (all brands view)
			router.push(userDefaultHome);
			setPreservedPath(null);
			setPreservedBrandId(null);
		} else if (prevLevel === "brand") {
			// Brand-level lock: go to brand home (preserve brand)
			router.push(brandDefaultHome);
			setPreservedPath(null);
		} else if (prevLevel === "soft" && preservedPath) {
			// Soft lock: restore exact path
			router.push(preservedPath);
		}
		
		// Clear preserved state
		setPreservedPageState({});
		
		// Notify server
		fetch("/api/auth/unlock", { method: "POST" }).catch(() => {});
	}, [lockLevel, preservedPath, userDefaultHome, brandDefaultHome, router]);
	
	// =============================================
	// Force Lock
	// =============================================
	
	const forceLock = useCallback(() => {
		applyLock("soft");
	}, [applyLock]);
	
	// =============================================
	// Save Page State
	// =============================================
	
	const savePageState = useCallback((state: Record<string, unknown>) => {
		setPreservedPageState(prev => ({ ...prev, ...state }));
	}, []);
	
	// =============================================
	// Inactivity Check Loop
	// =============================================
	
	// Restore state from session storage on mount (only once)
	useEffect(() => {
		// Don't restore lock state on home/login page
		if (isDisabledPage) return;
		
		try {
			const stored = sessionStorage.getItem(STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (parsed.lockLevel && parsed.lockLevel !== "none") {
					setLockLevel(parsed.lockLevel);
					setIsLocked(true);
					setPreservedPath(parsed.preservedPath);
					setPreservedBrandId(parsed.preservedBrandId);
					setPreservedPageState(parsed.preservedPageState || {});
				}
			}
		} catch (error) {
			console.debug("Failed to restore session state:", error);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run on mount
	
	// Start inactivity check interval
	useEffect(() => {
		// Skip inactivity checks on disabled pages (home/login)
		if (isDisabledPage) return;
		
		checkIntervalRef.current = setInterval(() => {
			const now = new Date();
			const inactivityMs = now.getTime() - lastActivityAt.getTime();
			const newLevel = calculateLockLevel(inactivityMs);
			
			// Update seconds until next lock
			let nextThreshold = INACTIVITY_THRESHOLDS.NONE;
			if (inactivityMs < INACTIVITY_THRESHOLDS.NONE) {
				nextThreshold = INACTIVITY_THRESHOLDS.NONE;
			} else if (inactivityMs < INACTIVITY_THRESHOLDS.SOFT) {
				nextThreshold = INACTIVITY_THRESHOLDS.SOFT;
			} else if (inactivityMs < INACTIVITY_THRESHOLDS.BRAND) {
				nextThreshold = INACTIVITY_THRESHOLDS.BRAND;
			}
			
			const secondsLeft = Math.max(0, Math.floor((nextThreshold - inactivityMs) / 1000));
			setSecondsUntilLock(secondsLeft);
			
			// Apply lock if threshold crossed
			if (newLevel !== "none" && newLevel !== lockLevel && !isLocked) {
				applyLock(newLevel);
			}
		}, CHECK_INTERVAL);
		
		return () => {
			if (checkIntervalRef.current) {
				clearInterval(checkIntervalRef.current);
			}
		};
	}, [lastActivityAt, lockLevel, isLocked, calculateLockLevel, applyLock, isDisabledPage]);
	
	// =============================================
	// Activity Event Listeners
	// =============================================
	
	useEffect(() => {
		// Skip activity listeners on disabled pages (home/login)
		if (isDisabledPage) return;
		
		const events = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "click"];
		
		const handleActivity = () => {
			recordActivity();
		};
		
		// Add throttled listener
		let throttleTimer: NodeJS.Timeout | null = null;
		const throttledHandler = () => {
			if (!throttleTimer) {
				handleActivity();
				throttleTimer = setTimeout(() => {
					throttleTimer = null;
				}, 1000); // Throttle to once per second
			}
		};
		
		events.forEach(event => {
			window.addEventListener(event, throttledHandler, { passive: true });
		});
		
		// Also track visibility changes
		const handleVisibility = () => {
			if (document.visibilityState === "visible") {
				// Check if we should be locked after tab was hidden
				const now = new Date();
				const inactivityMs = now.getTime() - lastActivityAt.getTime();
				const newLevel = calculateLockLevel(inactivityMs);
				
				if (newLevel !== "none" && !isLocked) {
					applyLock(newLevel);
				}
			}
		};
		
		document.addEventListener("visibilitychange", handleVisibility);
		
		return () => {
			events.forEach(event => {
				window.removeEventListener(event, throttledHandler);
			});
			document.removeEventListener("visibilitychange", handleVisibility);
		};
	}, [recordActivity, lastActivityAt, isLocked, calculateLockLevel, applyLock, isDisabledPage]);
	
	// =============================================
	// Context Value
	// =============================================
	
	const value: SessionActivityContextValue = {
		lockLevel,
		isLocked,
		lastActivityAt,
		secondsUntilLock,
		preservedPath,
		preservedBrandId,
		preservedPageState,
		recordActivity,
		unlock,
		forceLock,
		savePageState,
	};
	
	return (
		<SessionActivityContext.Provider value={value}>
			{children}
		</SessionActivityContext.Provider>
	);
}

// =============================================
// Hook for checking if activity recording should be disabled
// =============================================

export function useIsActivityDisabled() {
	const pathname = usePathname();
	
	// Don't record activity on login/auth pages
	const disabledPaths = ["/", "/login", "/signup", "/auth", "/logout"];
	
	// Exact match for "/" (home page), startsWith for others
	return disabledPaths.some(path =>
		path === "/" ? pathname === "/" : pathname?.startsWith(path)
	);
}
